import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";

/**
 * deskmanager.js — AI Voice System product page
 * Content sourced 1:1 from "AI VOICE SYSTEM — OVERVIEW"
 *
 * Drop-in page component for a MERN app.
 * No external UI libraries required — styles are scoped in the injected
 * <style> tag below (id="dm-styles"), so this file is self-contained.
 *
 * Usage (React Router example):
 *   import DeskManager from "./pages/deskmanager";
 *   <Route path="/ai-voice" element={<DeskManager />} />
 */

const SECTIONS = [
  {
    num: "01",
    sidebarTitle: "THE PAIN",
    sidebarBody: "Every missed call is a missed job.",
    title: "Missed Calls Cost You Jobs.",
    redHeadline:
      "You are busy working. The phone rings. You miss it. The customer calls the next guy.",
    bullets: [
      "You lose money.",
      "You waste time on spam calls.",
      "You forget to call people back.",
    ],
    boxTitle: "THE PROBLEM",
    boxBody:
      "You cannot grow if you are missing calls. But you cannot sit by the phone all day.",
    isLast: false,
  },
  {
    num: "02",
    sidebarTitle: "THE FIX",
    sidebarBody: "The phone is always answered.",
    title: "A Robot That Answers The Phone.",
    redHeadline:
      "We give you an AI assistant that never sleeps, never takes a break, and sounds human.",
    bullets: [
      "It answers every call instantly.",
      "It talks to the customer for you.",
      "It books jobs straight onto your calendar.",
    ],
    boxTitle: "THE SOLUTION",
    boxBody:
      "You just wake up and see new jobs booked. The AI handles the talking.",
    isLast: false,
  },
  {
    num: "03",
    sidebarTitle: "THE OFFER",
    sidebarBody: "We waived the $450 setup fee for you today.",
    title: "Get Your Time Back Today.",
    redHeadline: "Stop paying for missed jobs. Let the AI handle the phone.",
    bullets: null, // custom pricing bullets rendered inline
    boxTitle: "SECRET COUPON",
    boxBody:
      "Want 30-40% off the monthly price? We only have 5 coupons left. Apply now to see if you qualify.",
    isLast: true,
  },
];

function useReveal() {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      el.classList.add("dm-in");
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("dm-in");
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return ref;
}

function Section({ section }) {
  const ref = useReveal();
  return (
    <div className="dm-section dm-reveal" ref={ref}>
      {/* Large title */}
      <div className="dm-title-area">
        <h2 className="dm-title">{section.title}</h2>
      </div>

      {/* Divider */}
      <div className="dm-divider" />

      {/* Two-column body */}
      <div className="dm-body-grid">
        {/* Left column */}
        <div className="dm-main-col">
          <h3 className="dm-red-headline">{section.redHeadline}</h3>

          {/* Standard bullet list */}
          {section.bullets && (
            <ul className="dm-bullets">
              {section.bullets.map((b) => (
                <li key={b} className="dm-bullet">
                  <span className="dm-sq" aria-hidden="true" />
                  <span className="dm-bullet-text">{b}</span>
                </li>
              ))}
            </ul>
          )}

          {/* Pricing bullets — page 3 only */}
          {section.isLast && (
            <ul className="dm-bullets">
              <li className="dm-bullet">
                <span className="dm-sq" aria-hidden="true" />
                <span className="dm-bullet-text">
                  Setup Fee:{" "}
                  <span className="dm-strikethrough">$450</span>{" "}
                  <span className="dm-waived">WAIVED</span>
                </span>
              </li>
              <li className="dm-bullet">
                <span className="dm-sq" aria-hidden="true" />
                <span className="dm-bullet-text">Monthly: $297</span>
              </li>
            </ul>
          )}

          {/* Red box */}
          <div className="dm-red-box">
            <span className="dm-box-title">{section.boxTitle}</span>
            <p className="dm-box-body">{section.boxBody}</p>
            {/* CTA on last section */}
            {section.isLast && (
              <Link to="/aideskmanager" className="dm-cta-btn">
                CLAIM YOUR TERRITORY →
              </Link>
            )}
          </div>
        </div>

        {/* Vertical rule */}
        <div className="dm-vrule" aria-hidden="true" />

        {/* Right sidebar */}
        <div className="dm-sidebar">
          <div className="dm-sidebar-num">{section.num}</div>
          <div className="dm-sidebar-label">{section.sidebarTitle}</div>
          <p className="dm-sidebar-body">{section.sidebarBody}</p>
        </div>
      </div>
    </div>
  );
}

export default function DeskManager() {
  return (
    <div className="dm-root">
      <style id="dm-styles">{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap');

        /* ===== ROOT ===== */
        .dm-root {
          background: #ffffff;
          color: #111111;
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Helvetica Neue', Arial, sans-serif;
          line-height: 1.6;
          overflow-x: hidden;
        }
        .dm-root * { box-sizing: border-box; margin: 0; padding: 0; }

        /* ===== REVEAL ===== */
        .dm-reveal {
          opacity: 0;
          transform: translateY(24px);
          transition: opacity 0.65s ease, transform 0.65s cubic-bezier(0.2, 0.7, 0.3, 1);
        }
        .dm-reveal.dm-in {
          opacity: 1;
          transform: translateY(0);
        }

        /* ===== SECTION ===== */
        .dm-section {
          max-width: 900px;
          margin: 0 auto;
          padding: 80px 40px;
          border-bottom: 1px solid #e8e8e8;
        }
        .dm-section:last-child {
          border-bottom: none;
        }

        /* ===== TITLE ===== */
        .dm-title-area {
          padding-bottom: 36px;
        }
        .dm-title {
          font-size: clamp(36px, 6vw, 64px);
          font-weight: 800;
          line-height: 1.05;
          letter-spacing: -0.02em;
          color: #111111;
          max-width: 14ch;
        }

        /* ===== DIVIDER ===== */
        .dm-divider {
          height: 1px;
          background: #111111;
          margin-bottom: 48px;
        }

        /* ===== BODY GRID ===== */
        .dm-body-grid {
          display: grid;
          grid-template-columns: 1fr 1px 220px;
          gap: 0 40px;
          align-items: start;
        }

        /* ===== MAIN COLUMN ===== */
        .dm-main-col {
          padding-right: 8px;
        }
        .dm-red-headline {
          font-size: clamp(20px, 2.8vw, 26px);
          font-weight: 700;
          line-height: 1.25;
          color: #d0201a;
          margin-bottom: 28px;
        }

        /* ===== BULLETS ===== */
        .dm-bullets {
          list-style: none;
          display: flex;
          flex-direction: column;
          gap: 14px;
          margin-bottom: 32px;
        }
        .dm-bullet {
          display: flex;
          align-items: flex-start;
          gap: 12px;
        }
        .dm-sq {
          flex-shrink: 0;
          width: 12px;
          height: 12px;
          background: #d0201a;
          margin-top: 4px;
        }
        .dm-bullet-text {
          font-size: 15px;
          font-weight: 700;
          color: #111111;
          line-height: 1.5;
        }

        /* Pricing specials */
        .dm-strikethrough {
          text-decoration: line-through;
          color: #888888;
          font-weight: 700;
        }
        .dm-waived {
          color: #d0201a;
          font-weight: 700;
        }

        /* ===== RED BOX ===== */
        .dm-red-box {
          background: #d0201a;
          padding: 24px 28px 28px;
        }
        .dm-box-title {
          display: block;
          font-size: 13px;
          font-weight: 800;
          letter-spacing: 0.1em;
          text-transform: uppercase;
          color: #ffffff;
          margin-bottom: 12px;
        }
        .dm-box-body {
          font-size: 15px;
          font-weight: 700;
          line-height: 1.6;
          color: #ffffff;
        }

        /* ===== CTA BUTTON (page 3) ===== */
        .dm-cta-btn {
          display: inline-block;
          margin-top: 20px;
          background: #ffffff;
          color: #111111;
          font-size: 13px;
          font-weight: 800;
          letter-spacing: 0.07em;
          text-transform: uppercase;
          text-decoration: none;
          padding: 13px 24px;
          transition: background 0.2s ease, color 0.2s ease, transform 0.2s ease;
        }
        .dm-cta-btn:hover {
          background: #111111;
          color: #ffffff;
          transform: translateY(-1px);
        }
        .dm-cta-btn:focus-visible {
          outline: 2px solid #ffffff;
          outline-offset: 3px;
        }

        /* ===== VERTICAL RULE ===== */
        .dm-vrule {
          width: 1px;
          background: #cccccc;
          align-self: stretch;
          min-height: 180px;
        }

        /* ===== SIDEBAR ===== */
        .dm-sidebar {
          padding-left: 8px;
        }
        .dm-sidebar-num {
          font-size: clamp(64px, 9vw, 96px);
          font-weight: 900;
          line-height: 0.9;
          color: #d0201a;
          letter-spacing: -0.03em;
          margin-bottom: 14px;
        }
        .dm-sidebar-label {
          font-size: 12px;
          font-weight: 700;
          letter-spacing: 0.1em;
          text-transform: uppercase;
          color: #111111;
          margin-bottom: 12px;
        }
        .dm-sidebar-body {
          font-size: 14px;
          line-height: 1.7;
          color: #555555;
        }

        /* ===== RESPONSIVE ===== */
        @media (max-width: 700px) {
          .dm-section { padding: 56px 20px; }

          .dm-body-grid {
            grid-template-columns: 1fr;
            gap: 36px 0;
          }
          .dm-vrule { display: none; }
          .dm-sidebar {
            padding-left: 0;
            border-top: 1px solid #e0e0e0;
            padding-top: 28px;
          }
          .dm-sidebar-num { font-size: 72px; }
        }

        @media (prefers-reduced-motion: reduce) {
          .dm-reveal {
            opacity: 1;
            transform: none;
            transition: none;
          }
        }
      `}</style>

      {SECTIONS.map((section) => (
        <Section section={section} key={section.num} />
      ))}
    </div>
  );
}
